/**
 * Canonical "is this a safe, same-origin, relative in-app path?" predicate.
 *
 * One shared check for every surface that turns an untrusted notification
 * payload into a navigation:
 *   - native push tap handler (`usePushNotifications`)        -> react-router navigate()
 *   - service worker `notificationclick` (`service-worker.ts`) -> new URL() + clients.openWindow()
 *   - in-page relay of SW nav messages (`WebPushInitializer`)  -> react-router navigate()
 *
 * Centralizing this kills the silent-drift risk of three hand-rolled copies,
 * where one gets hardened and the others quietly don't.
 *
 * Security context: the service-worker consumer feeds the result through the
 * real WHATWG URL parser (`new URL(path, origin)`), so the predicate must
 * defend against open-redirect smuggling — protocol-relative `//host`, embedded
 * scheme `://`, backslash normalization (`\` -> `/`), and the tab/newline/CR
 * characters browsers strip out of URLs before parsing — not merely the
 * happy-path `startsWith('/')` that react-router alone would tolerate.
 *
 * Returns a type guard so callers can navigate without a second `typeof` check.
 *
 * @param value - Untrusted candidate (e.g. notification `data.url` / `data.route`).
 * @returns true iff `value` is a string safe to hand to in-app navigation.
 */
export function isSafeInAppPath(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.startsWith('/') && // relative, anchored to our origin
    !value.startsWith('//') && // not protocol-relative (`//evil.com`)
    !value.includes('://') && // no embedded scheme (`https://`, `javascript://`)
    !value.includes('\\') && // browsers normalize `\` -> `/`, reopening `//evil`
    !/[\t\n\r]/.test(value) // browsers strip these from URLs before parsing
  );
}

/**
 * Shared fallback destination for a notification tap whose deep link is missing
 * or fails the `isSafeInAppPath` gate. One literal across ALL tap surfaces:
 * the service worker `notificationclick` handler, the native FCM tap handler
 * (`usePushNotifications`), and the OneSignal `click` handler
 * (`useOneSignalNotificationEvents`).
 *
 * Why '/home' and not '/notifications': '/home' is guest-accessible, whereas
 * '/notifications' is wrapped in `ProtectedRoute` and renders null for a
 * signed-out or expired-session viewer - so it can never be a blind fallback
 * (the tap would dead-end on a blank screen). Engagement pushes always carry an
 * explicit deep-link url, so this net only ever catches a malformed or legacy
 * payload; sending those to the guest-safe home is the safe floor.
 */
export const NOTIFICATION_TAP_FALLBACK = '/home';
